import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from "react-router";
import axios from 'axios';
import './Login.css';
import myImage from '../../assets/aeryum_logo_nobackground.png';
import hero from '../../assets/LoginBackground.JPG';
import { useUser } from '../../context/UserContext';
import { getAuthToken, storeAuthToken } from '../../utils/authStorage';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState(null);
  const { loadUser } = useUser();
  const redirectTarget = getRedirectTarget(location.search);

  useEffect(() => {
    // Si el usuario ya tiene sesión activa, redirigirlo automáticamente
    const token = getAuthToken();
    if (token) {
      navigate(redirectTarget, { replace: true });
    }
  }, [navigate, redirectTarget]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post("/auth/login", {
        email,
        password,
      });

      const token = response.data.token;
      storeAuthToken(token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      await loadUser();
      navigate(redirectTarget, { replace: true });
    } catch (error) {
      if (!error.response) {
        setError("Connection error. Please check your network and try again.");
      } else if (error.response.status === 401) {
        setError("Incorrect email or password.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    setForgotEmail(email);
    setForgotMessage(null);
    setForgotOpen(true);
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setForgotLoading(true);
    setForgotMessage(null);
    try {
      const response = await axios.post('/auth/forgot-password', {
        email: forgotEmail.trim(),
      });
      setForgotMessage({
        type: 'success',
        text: response.data?.message
          || 'If an account exists, a password reset link has been sent.',
      });
    } catch (requestError) {
      setForgotMessage({
        type: 'error',
        text: requestError.response?.data?.message
          || 'We could not process the request. Please try again.',
      });
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <main className="login">
      <div className="login__shell">
        {/* Panel izquierdo */}
        <section className="login__left" aria-labelledby="welcome-title">
          <header className="login__brand">
            <img src={myImage} alt="Aeryum" className="login__logo" />
          </header>

          <div className="login__copy">
            <h1 id="welcome-title" className="login__title">Hi, Welcome!</h1>
            <p className="login__subtitle">
              We transform drone images into valuable insights.
              Access clear and actionable reports anytime.
            </p>
          </div>

          <form className="login__form" onSubmit={handleSubmit}>
            <label className="login__label" htmlFor="email">Your Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Insert your email here"
              className="login__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label className="login__label" htmlFor="password">Your Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              className={`login__input${error ? ' login__input--error' : ''}`}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              required
            />
            {error && <p className="login__error">{error}</p>}

            <div className="login__row">
              <button
                type="button"
                className="login__link"
                onClick={openForgotPassword}
              >
                Forgot Password
              </button>
            </div>

            <button type="submit" className="login__submit" disabled={loading}>
              {loading ? <span className="login__spinner" /> : 'Sign in'}
            </button>

            <p className="login__signup">
              Don’t have an account? <a href="/signup">Contact our team to get one</a>
            </p>
          </form>
        </section>

        {/* Panel derecho */}
        <aside className="login__right" role="img" aria-label="Industrial night city">
          <img src={hero} alt="" className="login__bg" />
          <div className="login__card">
            <p>
              Access your projects, inspections, and findings in one place. Gain valuable insights to make smarter and faster decisions.
            </p>
          </div>
        </aside>
      </div>

      {forgotOpen && (
        <div className="login-modal__backdrop" role="presentation">
          <section
            className="login-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
          >
            <button
              type="button"
              className="login-modal__close"
              aria-label="Close"
              onClick={() => setForgotOpen(false)}
            >
              ×
            </button>
            <h2 id="forgot-password-title">Reset your password</h2>
            <p>Enter your account email and we will send you a secure reset link.</p>
            <form onSubmit={handleForgotPassword}>
              <label className="login__label" htmlFor="forgot-email">Your Email</label>
              <input
                id="forgot-email"
                type="email"
                autoComplete="email"
                className="login__input"
                value={forgotEmail}
                onChange={(event) => setForgotEmail(event.target.value)}
                required
                autoFocus
              />
              {forgotMessage && (
                <p className={`login__message login__message--${forgotMessage.type}`}>
                  {forgotMessage.text}
                </p>
              )}
              <button type="submit" className="login__submit" disabled={forgotLoading}>
                {forgotLoading ? <span className="login__spinner" /> : 'Send reset link'}
              </button>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}

function getRedirectTarget(search) {
  const fallback = "/projects";
  const params = new URLSearchParams(search);
  const redirect = params.get("redirect");

  if (!redirect || !redirect.startsWith("/") || redirect.startsWith("//")) {
    return fallback;
  }

  if (redirect.startsWith("/login") || redirect.startsWith("/reset-password")) {
    return fallback;
  }

  return redirect;
}

export default Login;
